import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { API } from '../../helpers';
import './big-screen.css';

const refreshInterval = 60 * 1000;

const emptyData = {
  summary: {},
  daily_trend: [],
  model_top: [],
  channel_top: [],
  provider_top: [],
  model_providers: [],
  token_model_top: [],
  programming_token_top: [],
};

const colors = ['#14b8a6', '#38bdf8', '#22c55e', '#f59e0b', '#ef4444', '#94a3b8'];

const toNumber = (value) => {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number : 0;
};

const formatNumber = (value) => {
  const number = toNumber(value);
  if (number >= 100000000) return `${(number / 100000000).toFixed(2)}亿`;
  if (number >= 10000) return `${(number / 10000).toFixed(2)}万`;
  return number.toLocaleString('zh-CN');
};

const formatMoney = (value, currency = 'CNY') => {
  const number = toNumber(value);
  const prefix = currency === 'USD' ? '$' : '¥';
  if (number >= 10000) return `${prefix}${(number / 10000).toFixed(2)}万`;
  return `${prefix}${number.toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const chartTextStyle = {
  color: '#dbe7f3',
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", "Microsoft YaHei", sans-serif',
};

const axisLine = {
  lineStyle: {
    color: 'rgba(148, 163, 184, 0.22)',
  },
};

const splitLine = {
  lineStyle: {
    color: 'rgba(148, 163, 184, 0.12)',
  },
};

const tooltip = {
  trigger: 'axis',
  backgroundColor: 'rgba(15, 23, 42, 0.94)',
  borderColor: 'rgba(56, 189, 248, 0.22)',
  textStyle: chartTextStyle,
};

const panelOptionBase = {
  color: colors,
  grid: {
    left: 42,
    right: 18,
    top: 34,
    bottom: 30,
  },
  textStyle: chartTextStyle,
};

const BigScreen = () => {
  const [screenData, setScreenData] = useState(emptyData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [clock, setClock] = useState(new Date());
  const screenKey = useMemo(() => new URLSearchParams(window.location.search).get('key') || '', []);

  const fetchData = useCallback(async () => {
    if (!screenKey) {
      setError('缺少数据大屏访问密钥');
      setLoading(false);
      return;
    }
    try {
      const res = await API.get('/api/public/dashboard/screen', {
        params: { key: screenKey },
      });
      if (res?.data?.success) {
        setScreenData(res.data.data || emptyData);
        setError('');
      } else {
        setError(res?.data?.message || '数据加载失败');
      }
    } catch (err) {
      setError(err?.message || '数据加载失败');
    } finally {
      setLoading(false);
    }
  }, [screenKey]);

  useEffect(() => {
    fetchData();
    const dataTimer = setInterval(fetchData, refreshInterval);
    const clockTimer = setInterval(() => setClock(new Date()), 1000);
    return () => {
      clearInterval(dataTimer);
      clearInterval(clockTimer);
    };
  }, [fetchData]);

  const summary = screenData.summary || {};
  const dailyTrend = useMemo(() => screenData.daily_trend || [], [screenData.daily_trend]);
  const modelTop = useMemo(() => screenData.model_top || [], [screenData.model_top]);
  const providerTop = useMemo(
    () => screenData.provider_top || screenData.channel_top || [],
    [screenData.provider_top, screenData.channel_top]
  );
  const modelProviders = screenData.model_providers || [];
  const tokenModelTop = screenData.token_model_top || [];
  const programmingTokenTop = screenData.programming_token_top || [];

  const trendOption = useMemo(
    () => ({
      ...panelOptionBase,
      tooltip,
      legend: {
        right: 12,
        top: 0,
        textStyle: { color: '#9fb2c6' },
        itemWidth: 10,
        itemHeight: 10,
      },
      xAxis: {
        type: 'category',
        data: dailyTrend.map((item) => item.date?.slice(5) || ''),
        axisLine,
        axisTick: { show: false },
        axisLabel: { color: '#8fa3b8' },
      },
      yAxis: [
        {
          type: 'value',
          name: '请求',
          nameTextStyle: { color: '#8fa3b8' },
          splitLine,
          axisLine,
          axisLabel: { color: '#8fa3b8' },
        },
        {
          type: 'value',
          name: 'Token',
          nameTextStyle: { color: '#8fa3b8' },
          splitLine: { show: false },
          axisLine,
          axisLabel: {
            color: '#8fa3b8',
            formatter: (value) => formatNumber(value),
          },
        },
      ],
      series: [
        {
          name: '请求数',
          type: 'bar',
          barWidth: 12,
          data: dailyTrend.map((item) => toNumber(item.request_count)),
          itemStyle: {
            borderRadius: [4, 4, 0, 0],
            color: '#14b8a6',
          },
        },
        {
          name: 'Token',
          type: 'line',
          yAxisIndex: 1,
          smooth: true,
          symbolSize: 6,
          data: dailyTrend.map((item) => toNumber(item.tokens)),
          lineStyle: { width: 3, color: '#38bdf8' },
          itemStyle: { color: '#38bdf8' },
          areaStyle: { color: 'rgba(56, 189, 248, 0.1)' },
        },
      ],
    }),
    [dailyTrend]
  );

  const modelOption = useMemo(
    () => ({
      ...panelOptionBase,
      tooltip,
      grid: { left: 126, right: 18, top: 12, bottom: 18 },
      xAxis: {
        type: 'value',
        splitLine,
        axisLine,
        axisLabel: {
          color: '#8fa3b8',
          formatter: (value) => formatNumber(value),
        },
      },
      yAxis: {
        type: 'category',
        inverse: true,
        data: modelTop.map((item) => item.model_name || 'unknown'),
        axisLine,
        axisTick: { show: false },
        axisLabel: { color: '#dbe7f3', width: 116, overflow: 'truncate' },
      },
      series: [
        {
          type: 'bar',
          barWidth: 12,
          data: modelTop.map((item) => toNumber(item.tokens)),
          itemStyle: {
            borderRadius: [0, 6, 6, 0],
            color: '#22c55e',
          },
        },
      ],
    }),
    [modelTop]
  );

  const channelOption = useMemo(
    () => ({
      color: colors,
      tooltip: {
        trigger: 'item',
        backgroundColor: 'rgba(15, 23, 42, 0.94)',
        borderColor: 'rgba(20, 184, 166, 0.22)',
        textStyle: chartTextStyle,
      },
      legend: {
        orient: 'vertical',
        right: 4,
        top: 'center',
        textStyle: { color: '#9fb2c6' },
        itemWidth: 10,
        itemHeight: 10,
        formatter: (name) => (name.length > 12 ? `${name.slice(0, 12)}...` : name),
      },
      series: [
        {
          type: 'pie',
          radius: ['48%', '72%'],
          center: ['36%', '50%'],
          avoidLabelOverlap: true,
          label: { show: false },
          emphasis: {
            label: {
              show: true,
              color: '#f8fafc',
              formatter: '{d}%',
              fontSize: 16,
              fontWeight: 700,
            },
          },
          data: providerTop.map((item) => ({
            name: item.provider_name || item.channel_name || `Channel #${item.channel_id || 0}`,
            value: toNumber(item.tokens),
          })),
        },
      ],
    }),
    [providerTop]
  );

  const latestTime = screenData.generated_at
    ? new Date(screenData.generated_at * 1000)
    : clock;

  return (
    <main className="big-screen">
      <div className="big-screen__backdrop" />
      <header className="big-screen__header">
        <div>
          <div className="big-screen__eyebrow">Gateway Operations</div>
          <h1>网关运营数据大屏</h1>
        </div>
        <div className="big-screen__meta">
          <span className={error ? 'big-screen__status error' : 'big-screen__status'}>
            {error ? '数据异常' : loading ? '加载中' : '实时运行'}
          </span>
          <span>{clock.toLocaleString('zh-CN', { hour12: false })}</span>
        </div>
      </header>

      {error && <div className="big-screen__error">{error}</div>}

      <section className="big-screen__kpis">
        <Kpi title="今日请求数" value={formatNumber(summary.today_requests)} tone="teal" />
        <Kpi title="昨日请求数" value={formatNumber(summary.yesterday_requests)} tone="sky" />
        <Kpi title="本月请求数" value={formatNumber(summary.month_requests)} tone="green" />
        <Kpi title="本月 Token" value={formatNumber(summary.month_tokens)} tone="amber" />
        <Kpi
          title="本月消费"
          value={formatMoney(summary.month_amount_cny, 'CNY')}
          sub={formatMoney(summary.month_amount_usd, 'USD')}
          tone="red"
        />
        <Kpi title="近7日 Token" value={formatNumber(summary.total_tokens_7d)} tone="muted" />
      </section>

      <section className="big-screen__grid">
        <Panel className="big-screen__panel--wide" title="近14日调用趋势">
          <ReactECharts option={trendOption} className="big-screen__chart" notMerge lazyUpdate />
        </Panel>

        <Panel title="上游 Provider 占比">
          <ReactECharts option={channelOption} className="big-screen__chart" notMerge lazyUpdate />
        </Panel>

        <Panel title="模型 Token 排行">
          <ReactECharts option={modelOption} className="big-screen__chart" notMerge lazyUpdate />
        </Panel>

        <Panel className="big-screen__panel--table" title="模型上游贡献">
          <div className="big-screen__table big-screen__table--provider">
            <div className="big-screen__table-head">
              <span>模型</span>
              <span>Provider</span>
              <span>渠道数</span>
              <span>Token</span>
              <span>请求</span>
            </div>
            {modelProviders.length === 0 ? (
              <div className="big-screen__empty">暂无数据</div>
            ) : (
              modelProviders.map((item, index) => (
                <div className="big-screen__table-row" key={`${item.model_name}-${item.provider_key}-${index}`}>
                  <span title={item.model_name}>{item.model_name || 'unknown'}</span>
                  <span title={item.provider_name}>{item.provider_name || item.provider_key || '-'}</span>
                  <span>{formatNumber(item.channel_count)}</span>
                  <strong>{formatNumber(item.tokens)}</strong>
                  <span>{formatNumber(item.request_count)}</span>
                </div>
              ))
            )}
          </div>
        </Panel>

        <Panel className="big-screen__panel--table" title="令牌模型用量排行">
          <div className="big-screen__table big-screen__table--token">
            <div className="big-screen__table-head">
              <span>令牌</span>
              <span>项目</span>
              <span>模型</span>
              <span>Token</span>
              <span>请求</span>
            </div>
            {tokenModelTop.length === 0 ? (
              <div className="big-screen__empty">暂无数据</div>
            ) : (
              tokenModelTop.map((item, index) => (
                <div className="big-screen__table-row" key={`${item.token_name}-${item.model_name}-${index}`}>
                  <span>{item.token_name || '***'}</span>
                  <span>{item.project_number || '-'}</span>
                  <span title={item.model_name}>{item.model_name || 'unknown'}</span>
                  <strong>{formatNumber(item.tokens)}</strong>
                  <span>{formatNumber(item.request_count)}</span>
                </div>
              ))
            )}
          </div>
        </Panel>

        <Panel className="big-screen__panel--table" title="编程专项令牌排行">
          <div className="big-screen__table big-screen__table--programming">
            <div className="big-screen__table-head">
              <span>令牌</span>
              <span>项目</span>
              <span>Token</span>
              <span>请求</span>
              <span>消费</span>
            </div>
            {programmingTokenTop.length === 0 ? (
              <div className="big-screen__empty">暂无数据</div>
            ) : (
              programmingTokenTop.map((item, index) => (
                <div className="big-screen__table-row" key={`${item.token_name}-${index}`}>
                  <span>{item.token_name || '***'}</span>
                  <span>{item.project_number || '-'}</span>
                  <strong>{formatNumber(item.tokens)}</strong>
                  <span>{formatNumber(item.request_count)}</span>
                  <span>{formatMoney(item.amount_cny, 'CNY')}</span>
                </div>
              ))
            )}
          </div>
        </Panel>
      </section>

      <footer className="big-screen__footer">
        <span>{screenData.settlement_note || '数据每 60 秒刷新'}</span>
        <span>最后刷新：{latestTime.toLocaleTimeString('zh-CN', { hour12: false })}</span>
      </footer>
    </main>
  );
};

const Kpi = ({ title, value, sub, tone }) => (
  <article className={`big-screen__kpi big-screen__kpi--${tone}`}>
    <span>{title}</span>
    <strong>{value}</strong>
    {sub && <em>{sub}</em>}
  </article>
);

const Panel = ({ title, children, className = '' }) => (
  <section className={`big-screen__panel ${className}`}>
    <div className="big-screen__panel-title">{title}</div>
    {children}
  </section>
);

export default BigScreen;
